import { useEffect } from 'react';
import PropTypes from 'prop-types';
import useProgressBarState from './state/useProgressBarState';
import useMounted from '@/hooks/utility/useMounted';
import { classes } from '@/helpers/styling';

const ProgressBar = ({
  seek,
  onSeek,
  onMoveSlider,
  max,
  leftAndRightPadding,
  className,
  sliderTrackClassName,
  sliderProgressClassName,
  sliderThumbClassName,
  disableInteraction,
  forceStartPosition,
  vertical,
  alwaysShowThumb
}) => {
  // State is abastracted into this hook for cleanliness
  const {
    liveSliderPercentage,
    liveSliderSeek,
    dragged,
    // Styles
    progressStyles,
    thumbStyles,
    // Click handlers - the other click handlers are attached to the document
    // to allow the user to move their mouse freely when dragging
    handleClickStart,
    // Touch handlers
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    // Ref for tracking user dragging of the progress bar
    sliderRef
  } = useProgressBarState({
    seek,
    onSeek,
    max,
    leftAndRightPadding,
    vertical,
    forceStartPosition,
    alwaysShowThumb
  });

  const isMounted = useMounted();

  useEffect(() => {
    if (isMounted && dragged && onMoveSlider) {
      onMoveSlider({
        percentage: liveSliderPercentage,
        seek: liveSliderSeek
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveSliderSeek, dragged]);

  return (
    <div
      className={ classes(className, 'slider-container', {
        '--is-vertical': vertical,
        '--is-disabled': disableInteraction
      }) }
      onMouseDown={ disableInteraction ? undefined : handleClickStart }
      onTouchCancel={ disableInteraction ? undefined : handleTouchEnd }
      onTouchEnd={ disableInteraction ? undefined : handleTouchEnd }
      onTouchMove={ disableInteraction ? undefined : handleTouchMove }
      onTouchStart={ disableInteraction ? undefined : handleTouchStart }
      ref={ sliderRef }
      style={ vertical ? {
        paddingTop: `${ leftAndRightPadding }px`,
        paddingBottom: `${ leftAndRightPadding }px`
      } : {
        paddingLeft: `${ leftAndRightPadding }px`,
        paddingRight: `${ leftAndRightPadding }px`
      } }
    >
      <div className={ classes('slider', { '--is-vertical': vertical }) }>
        <div className={ classes(sliderTrackClassName, 'slider-track', { '--is-vertical': vertical }) } />
        <div
          className={ classes(sliderProgressClassName, 'slider-progress', { '--is-vertical': vertical }) }
          style={ progressStyles }
        />
        <div
          className={ classes(
            sliderThumbClassName,
            'slider-thumb',
            {
              '--is-vertical': vertical,
              '--is-disabled': disableInteraction
            }
          ) }
          style={ thumbStyles }
        />
      </div>
    </div>
  );
};

ProgressBar.propTypes = {
  seek: PropTypes.number,
  onSeek: PropTypes.func.isRequired,
  max: PropTypes.number,
  leftAndRightPadding: PropTypes.number,
  className: PropTypes.string,
  sliderTrackClassName: PropTypes.string,
  sliderProgressClassName: PropTypes.string,
  sliderThumbClassName: PropTypes.string,
  disableInteraction: PropTypes.bool,
  onMoveSlider: PropTypes.func,
  forceStartPosition: PropTypes.bool,
  vertical: PropTypes.bool,
  alwaysShowThumb: PropTypes.bool
};

ProgressBar.defaultProps = {
  seek: 0,
  max: 100,
  leftAndRightPadding: 24,
  className: '',
  sliderTrackClassName: '',
  sliderProgressClassName: '',
  sliderThumbClassName: '',
  disableInteraction: false,
  onMoveSlider: undefined,
  forceStartPosition: false,
  vertical: false,
  alwaysShowThumb: false
};

export default ProgressBar;